import React, { useState, } from 'react';
import { orderBy, uniq, } from 'lodash';
import { Button, Modal, ModalBody, ModalFooter, Input, Label, FormGroup, } from 'reactstrap';
import { toast } from 'react-toastify';

import firebase from '../firebase';
import useCollectionSubscription from './hooks/useCollectionSubscription';

const db = firebase.firestore();
const notificationsRef = db.collection('notifications');

// 同一セッション（タブ）内で一度閉じたお知らせは再表示しないための記録。
// sessionStorage なのでページ遷移・リロードでは残り、タブを閉じるとクリアされる。
const SESSION_KEY = 'closedNotificationIds';

const getSessionClosedIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || [];
  } catch(e) {
    return [];
  }
};

const addSessionClosedId = (id) => {
  const next = uniq([...getSessionClosedIds(), id]);
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  } catch(e) {
    // sessionStorage 不可の環境では無視（その場合は閉じてもセッション内再表示され得る）
  }
  return next;
};

// ログイン後に表示するお知らせポップアップ。
// popupStartAt〜popupEndAt の期間内で、まだ「今後表示しない」にしておらず、
// 同一セッション内でまだ閉じていないものを、開始日の降順で順次モーダル表示する。
export default function NotificationPopups (props) {
  const { user } = props;
  const now = new Date();
  const notifications = useCollectionSubscription(notificationsRef);
  const [sessionClosedIds, setSessionClosedIds] = useState(getSessionClosedIds);
  const dismissedIds = user?.dismissedNotificationIds || [];

  const popupNotifications = orderBy(
    notifications.filter((_) => {
      if(dismissedIds.includes(_.id)) return false;
      if(sessionClosedIds.includes(_.id)) return false;
      const start = _.popupStartAt?.toDate();
      const end = _.popupEndAt?.toDate();
      if(start == null && end == null) return false;
      if(start != null && start > now) return false;
      if(end != null && end < now) return false;
      return true;
    }),
    _ => (_.popupStartAt || _.publishedAt)?.toDate() || new Date(0),
    'desc',
  );

  if(popupNotifications.length === 0) return null;

  // 先頭の1件のみ表示。閉じるとセッションに記録され、次の対象が繰り上がって表示される。
  return (
    <NotificationPopup
      {...props}
      key={popupNotifications[0].id}
      notification={popupNotifications[0]}
      onClosed={id => setSessionClosedIds(addSessionClosedId(id))}
    />
  );
}

function NotificationPopup (props) {
  const { user, notification, onClosed } = props;
  const [dismissesForever, setDismissesForever] = useState(false);

  const onClose = async () => {
    if(dismissesForever) {
      try {
        await user.ref.update({
          dismissedNotificationIds: firebase.firestore.FieldValue.arrayUnion(notification.id),
        });
      } catch(e) {
        console.error(e);
        toast.error('失敗しました');
      }
    }
    onClosed(notification.id);
  };

  const { title, image } = notification;

  return (
    <Modal isOpen zIndex={2000}>
      <ModalBody>
        {
          image?.downloadUrl && (
            <div className="text-center mb-3">
              <img src={image.downloadUrl} alt={title} style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', }} />
            </div>
          )
        }
        <h5 className="text-center mb-0">{title}</h5>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <FormGroup check className="mb-0">
          <Label check>
            <Input type="checkbox" checked={dismissesForever} onChange={_ => setDismissesForever(_.target.checked)} />
            このお知らせを今後表示しない
          </Label>
        </FormGroup>
        <Button color="secondary" onClick={onClose}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  );
}
